import React, { useEffect } from "react";
import {
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormTextField from "components/form/FormTextField";
import StyledButton from "components/button/StyledButton";
import FormSelect from "components/form/FormSelect";
import { useCreateUserMutation, useUpdateUserMutation, useGetFirmsQuery, useGetUserQuery, useUpdateUserProfileMutation } from "api/api";
import { getErrorMessage } from "lib/utils/getErrorMessage";
import countriesAndStates from 'assets/countries_and_states.json'
import { getRegionsForCountry } from "pages/AddEcomPage/helpers";


interface UserFormProps {
  initialData?: any;
  roleName: string;
  isEditingSelf?: boolean;
  buttonText?: string;
  disabledFields?: string[];
  onSuccess?: () => void;
  onClose?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  initialData,
  roleName,
  isEditingSelf,
  buttonText,
  disabledFields,
  onSuccess,
  onClose,
}) => {
  const { data: firms, isLoading: firmsLoading } = useGetFirmsQuery();
  const { data: userInfo } = useGetUserQuery()
  const [createUser, { error: createError, isLoading: isCreating }] = useCreateUserMutation();
  const [updateOtherUser, { error: updateOtherUserError, isLoading: isUpdatingOtherUser }] = useUpdateUserMutation();
  const [updateOwnProfile, { error: updateOwnProfileError, isLoading: isUpdatingOwnProfile }] = useUpdateUserProfileMutation();

  const schema = z.object({
    firstName: z
      .string({ required_error: "First Name is required" })
      .min(1, { message: "First Name is required" }),
    lastName: z
      .string({ required_error: "Last Name is required" })
      .min(1, { message: "Last Name is required" }),
    email: z
      .string({ required_error: "Email is required" })
      .email({ message: "Invalid email format" }),
    firm: z.string().superRefine((val, ctx) => {
      if (!isEditingSelf && roleName !== 'Sub Super Admin' && (!val || val.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Firm is required",
        });
      }
    }),
    country: z.string().min(1, { message: "Country is required" }),
    address: z.string().min(1, { message: "Address is required" }),
    city: z.string().min(1, { message: "City is required" }),
    subnational: z.string().min(1, { message: "State / Province / Region is required" }),
    zipCode: z.string().min(1, { message: "Zip Code is required" }),
  }).required();

  const isEditing = !!initialData?.auth0_id;

  const error = updateOtherUserError || createError || updateOwnProfileError;

  const isUserFirmAdmin = userInfo?.site_role === "Firm Admin"

  const defaultFirm = initialData?.firm || (isUserFirmAdmin && userInfo?.firm) || ""

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: initialData?.first_name || "",
      lastName: initialData?.last_name || "",
      email: initialData?.email || "",
      firm: initialData?.firm || (isUserFirmAdmin && userInfo?.firm) || "",
      country: initialData?.country || "",
      address: initialData?.address || "",
      city: initialData?.city || "",
      subnational: initialData?.subnational || "",
      zipCode: initialData?.zipcode || "",
    },
  });

  useEffect(() => {
    if (defaultFirm && !initialData?.firm) {
      form.setValue("firm", defaultFirm);
    }
  }, [defaultFirm, form.setValue]);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (formData: any) => {
    try {
      const userData = {
        firm: formData.firm,
        address: formData.address,
        country: formData.country,
        subnational: formData.subnational,
        city: formData.city,
        zipcode: formData.zipCode,
        site_role: roleName,
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
      };

      for (const disabledField of (disabledFields || [])) {
        delete userData[disabledField];
      }

      if (isEditingSelf) {
        await updateOwnProfile({ ...userData, auth0_id: initialData.auth0_id }).unwrap();
        if (onSuccess) {
          onSuccess();
        }
      } else if (isEditing) {
        await updateOtherUser({ id: initialData.auth0_id, formData: userData }).unwrap();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        await createUser(userData).unwrap();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Error saving user:", error);
    }
  };

  const selectedCountry = watch("country");


  const inputLabels = [
    { name: "firstName", label: "First Name", component: "FormTextField" },
    { name: "lastName", label: "Last Name", component: "FormTextField" },
    { name: "email", label: "Email", component: "FormTextField" },
    ...(roleName !== "Sub Super Admin"
      ? [
        {
          // Annoying type fix
          name: "firm" as "firm",
          label: "Firm",
          component: "FormSelect",
          menuItems: firms?.map((firm) => firm.name) || [],
        },
      ]
      : []),
    { name: "country", label: "Country", component: "FormSelect", menuItems: countriesAndStates.map(c => c.name) },
    { name: "address", label: "Address", component: "FormTextField" },
    { name: "city", label: "City", component: "FormTextField" },
    {
      name: "subnational", label: "State / Province / Region", component: "FormSelect", menuItems: selectedCountry ?
        getRegionsForCountry(selectedCountry)
        : []
    },
    { name: "zipCode", label: "Zip Code", component: "FormTextField" },
  ] as const;

  const SELF_EDITING_OMITTED_FIELD = 'firm';
  const inputLabelsFiltered = inputLabels.filter(item => {
    const isFieldOmitted = item.name !== SELF_EDITING_OMITTED_FIELD;
    return isEditingSelf ? isFieldOmitted : true;
  })

  return (
    <form id="user-form" onSubmit={handleSubmit(onSubmit)}>
      {inputLabelsFiltered.map((inputLabel) => (
        <Controller
          key={inputLabel.name}
          name={inputLabel.name}
          control={control}
          render={({ field }) => (
            <Box>
              {inputLabel.component === "FormSelect" ? (
                <FormSelect
                  {...field}
                  label={inputLabel.label}
                  error={!!errors[inputLabel.name]}
                  helperText={errors[inputLabel.name]?.message}
                  menuItems={inputLabel.menuItems as unknown as string[]}
                  disabled={isSubmitting ||
                            firmsLoading ||
                            (inputLabel.name === 'subnational' && !selectedCountry) ||
                            (inputLabel.name === 'firm' && isUserFirmAdmin) ||
                            (disabledFields || []).includes(inputLabel.name)}
                />
              ) : (
                <FormTextField
                  {...field}
                  label={inputLabel.label}
                  error={!!errors[inputLabel.name]}
                  helperText={errors[inputLabel.name]?.message?.toString()}
                  disabled={isSubmitting || (disabledFields || []).includes(inputLabel.name)}
                />
              )}
            </Box>
          )}
        />
      ))}
      <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start", mb: 1 }}>
        <StyledButton label="Cancel" type="outlined" onClick={onClose} />
        <StyledButton
          form="user-form"
          label={buttonText ?? (isEditing ? "Update User" : "Add User")}
          type="filled"
          disabled={isCreating || isUpdatingOtherUser || isUpdatingOwnProfile}
          endAdornment={(isCreating || isUpdatingOtherUser || isUpdatingOwnProfile) && (
            <CircularProgress
              size={16}
              sx={{ color: '#fff' }}
            />
          )}
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      )}
    </form>
  );
};

export default UserForm;